import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../apiConfig';
import { useNavigate } from 'react-router-dom';
import NewsCard from './home/components/NewsCard';
import FeaturedVidCard from './home/components/FeaturedVidCard';
import VideoModal from './home/components/VideoModal';
import NoticeModal from './home/components/NoticeModal';
import './ListingPage.css';

const ListingPage = ({ type }) => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedYear, setSelectedYear] = useState('Year');
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [isNoticeModalOpen, setIsNoticeModalOpen] = useState(false);
    const itemsPerPage = 8; // Based on the user's grid image showing multiple rows

    const titles = {
        news: 'News',
        videos: 'Videos',
        notices: 'Notices'
    };

    const apiUrls = {
        news: `${API_BASE_URL}/news/`,
        videos: `${API_BASE_URL}/vedios/`,
        notices: `${API_BASE_URL}/specialNotices/`
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const langEl = document.getElementById('language');
                const language = langEl ? langEl.value : '1';
                const response = await axios.get(apiUrls[type] + language);
                
                let results = [];
                if (type === 'news') results = response.data.news || [];
                else if (type === 'videos') results = response.data.vedios || [];
                else if (type === 'notices') results = response.data.special_notices || [];
                
                setData(results);
                setFilteredData(results);
            } catch (error) {
                console.error(`Error fetching ${type}:`, error);
            }
            setLoading(false);
        };

        fetchData();
        window.scrollTo(0, 0);
    }, [type]);

    useEffect(() => {
        if (selectedYear === 'Year') {
            setFilteredData(data);
        } else {
            const filtered = data.filter(item => {
                const dateStr = item.publish_date || item.created_at || '';
                return dateStr.includes(selectedYear);
            });
            setFilteredData(filtered);
        }
        setCurrentPage(1);
    }, [selectedYear, data]);

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo(0, 0);
    };

    const handleVideoClick = (video) => {
        setSelectedVideo(video);
        setIsModalOpen(true);
    };

    const handleNoticeClick = (notice) => {
        setSelectedNotice(notice);
        setIsNoticeModalOpen(true);
    };

    const years = ['Year', ...new Set(data.map(item => {
        const date = item.publish_date || item.created_at || '';
        const match = date.match(/\d{4}/);
        return match ? match[0] : null;
    }).filter(Boolean))].sort((a, b) => b - a);

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="listing-body-div">
            {/* Immersive Hero Header */}
            <div className="listing-page-title-div">
                <div className="listing-text-div">
                    <h1 className="listing-page-title">{titles[type]}</h1>
                </div>
            </div>

            <div className="listing-content-wrap">
                {/* Refined Filter Bar */}
                <div className="listing-filter-div">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '14px', color: '#666' }}>
                            Showing {filteredData.length} {type} items
                        </span>
                    </div>
                    <div className="listing-input-field-div">
                        <select 
                            className="listing-year-select"
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}
                        >
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Card Grid */}
                <div className="listing-grid-div">
                    {currentItems.map((item, index) => (
                        <div key={index} className="listing-item-wrapper">
                            {type === 'news' || type === 'notices' ? (
                                <NewsCard 
                                    data={item} 
                                    onClick={type === 'notices' ? handleNoticeClick : null} 
                                />
                            ) : (
                                <FeaturedVidCard data={item} onClick={handleVideoClick} />
                            )}
                        </div>
                    ))}
                    {currentItems.length === 0 && (
                        <div className="no-results">No {type} found for the selected year.</div>
                    )}
                </div>

                {/* Modern Pagination */}
                {totalPages > 1 && (
                    <div className="listing-pagination-div">
                        <span 
                            className="listing-pagination-text-button"
                            onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                        >
                            Previous
                        </span>
                        
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {[...Array(totalPages)].map((_, i) => (
                                <div 
                                    key={i + 1}
                                    className={`listing-pagination-number ${currentPage === i + 1 ? 'active' : ''}`}
                                    onClick={() => paginate(i + 1)}
                                >
                                    {i + 1}
                                </div>
                            ))}
                        </div>
                        
                        <span 
                            className="listing-pagination-text-button"
                            onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                        >
                            Next
                        </span>
                    </div>
                )}
            </div>

            {/* Video Playback Modal */}
            <VideoModal 
                video={selectedVideo} 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
            />

            {/* Notice Playback Modal */}
            <NoticeModal 
                notice={selectedNotice} 
                isOpen={isNoticeModalOpen} 
                onClose={() => setIsNoticeModalOpen(false)} 
            />
        </div>
    );
};

export default ListingPage;
